import {
  approveAction as apiApproveAction,
  discardAction as apiDiscardAction,
  applyApprovedActions as apiApplyApprovedActions,
  discardAllProposedActions as apiDiscardAllProposedActions,
  getExecutionLogs as apiGetExecutionLogs,
  confirmKeepAction as apiConfirmKeepAction,
  revertAction as apiRevertAction,
  confirmAllAppliedActions as apiConfirmAllAppliedActions,
  revertAllAppliedActions as apiRevertAllAppliedActions,
  submitPrompt as apiSubmitPrompt,
  retrySessionInput as apiRetrySessionInput,
  regenerateAiResponse as apiRegenerateAiResponse,
  restoreSummary as apiRestoreSummary,
  SubmitPromptPayload,
} from '@/lib/api';
import { ApplyApprovedActionsResponse, DiscardAllActionsResponse, ConfirmAllActionsResponse, RevertAllActionsResponse, SessionInput } from '@/types/api';
import { toast } from 'sonner';
import { SessionState, ActionsSlice } from './state';
import { StateCreator } from 'zustand';

export const createActionsSlice: StateCreator<SessionState, [], [], ActionsSlice> = (set, get) => ({
  latestSessionInput: null,
  activePlanInput: null,
  activeSectionInputIds: null,
  isActionLoading: {},
  isBatchActionLoading: false,
  llmGenerationStatus: {
    sessionInputId: null,
    status: 'idle',
    isStreaming: false,
    error: null,
  },

  setActivePlanInputData: (input: SessionInput) => {
    set({ activePlanInput: input, activeSectionInputIds: null }); // Update data only, no navigation
  },
  setActivePlanInput: (input: SessionInput) => {
    set({ activePlanInput: input, activeSectionInputIds: null, activeMainPage: 'plan' }); // Update data + navigate to plan
  },
  setActiveSectionInputIds: (inputIds: string[] | null) => {
    set({ activeSectionInputIds: inputIds, activeMainPage: 'plan' }); // Set section for section-changes view
  },

  setLlmGenerationStarted: (sessionInputId: string, sessionId: string, isStreaming: boolean) => {
    set({
      llmGenerationStatus: {
        sessionInputId,
        status: 'started',
        isStreaming,
        error: null,
      },
    });
  },

  setLlmGenerationInProgress: (sessionInputId: string) => {
    set((state) => {
      // Only update if this is the current generation
      if (state.llmGenerationStatus.sessionInputId === sessionInputId) {
        return {
          llmGenerationStatus: {
            ...state.llmGenerationStatus,
            status: 'in-progress',
          },
        };
      }
      return state;
    });
  },

  setLlmGenerationEnded: (sessionInputId: string, error?: string) => {
    set((state) => {
      // Only update if this is the current generation
      if (state.llmGenerationStatus.sessionInputId === sessionInputId) {
        return {
          llmGenerationStatus: {
            ...state.llmGenerationStatus,
            status: 'ended',
            error: error || null,
          },
        };
      }
      return state;
    });
  },

  resetLlmGenerationStatus: () => {
    set({
      llmGenerationStatus: {
        sessionInputId: null,
        status: 'idle',
        isStreaming: false,
        error: null,
      },
    });
  },

  submitPrompt: async (payload: { user_prompt: string; execution_strategy: string; }) => {
    const currentSessionId = get().currentSessionId;
    const currentSessionDetails = get().currentSessionDetails;
    if (!currentSessionId || !currentSessionDetails) {
      const msg = "No active session to submit prompt.";
      set({ error: msg, currentSessionStatus: 'error' });
      toast.error(msg);
      return false;
    }
    set({ isLoading: true, error: null, lastSubmittedPrompt: payload.user_prompt });

    const existingInputsCount = currentSessionDetails.sessionInputs?.length || 0;
    const isFirstPrompt = existingInputsCount === 0;

    const templateId = isFirstPrompt
      ? currentSessionDetails.default_initial_context_template_id
      : currentSessionDetails.default_followup_context_template_id;
    
    const { adHocFiles, adHocFolders, adHocCommandOutputs } = get();
    const ad_hoc_context_definition = (adHocFiles.length > 0 || adHocFolders.length > 0 || adHocCommandOutputs.length > 0)
      ? JSON.stringify({ files: adHocFiles, folders: adHocFolders, command_outputs: adHocCommandOutputs })
      : undefined;

    try {
      const submitPayload: SubmitPromptPayload = {
        user_prompt: payload.user_prompt,
        execution_strategy: payload.execution_strategy,
        context_template_id: templateId || null,
        ad_hoc_context_definition: ad_hoc_context_definition,
      };
      
      // This is now a "fire-and-forget" call that returns the initial SessionInput.
      // The full plan will arrive via WebSocket.
      const initialSessionInput = await apiSubmitPrompt(currentSessionId, submitPayload);
      
      get().clearAdHocContext();

      set({
        latestSessionInput: initialSessionInput,
        activePlanInput: initialSessionInput,
        isLoading: false, // Set to false, but the UI might show a different loading state until the plan is ready.
      });

      await get().fetchSessionDetails(currentSessionId, 'refresh');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit prompt';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
      return false;
    }
  },

  retrySessionInput: async (inputId: string, payload: { user_prompt: string; execution_strategy?: string; }) => {
    const currentSessionId = get().currentSessionId;
    if (!currentSessionId) {
      const msg = "No active session to retry prompt.";
      set({ error: msg, currentSessionStatus: 'error' });
      toast.error(msg);
      return false;
    }

    set({ isLoading: true, error: null, editingRetryInputId: null });

    try {
      await apiRetrySessionInput(currentSessionId, inputId, {
        user_prompt: payload.user_prompt,
        execution_strategy: payload.execution_strategy || get().executionStrategy,
      });
      
      set({ isLoading: false });
      await get().fetchSessionDetails(currentSessionId, 'refresh');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retry prompt';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
      return false;
    }
  },

  regenerateAiResponse: async (inputId: string) => {
    const currentSessionId = get().currentSessionId;
    if (!currentSessionId) {
      const msg = "No active session to regenerate AI response.";
      toast.error(msg);
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      await apiRegenerateAiResponse(currentSessionId, inputId);

      set({ isLoading: false });
      await get().fetchSessionDetails(currentSessionId, 'refresh');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate AI response';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
      return false;
    }
  },

  approveAction: async (actionId: string) => {
    set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: true }, error: null }));
    try {
      const updatedAction = await apiApproveAction(actionId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action =>
            action.id === actionId ? updatedAction : action
          ),
        } : null,
        isActionLoading: { ...state.isActionLoading, [actionId]: false },
      }));
      toast.success(`Action ${actionId.substring(0,8)} approved.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to approve action ${actionId.substring(0,8)}`;
      set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: false }, error: errorMessage }));
      toast.error(errorMessage);
    }
  },

  discardAction: async (actionId: string) => {
    set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: true }, error: null }));
    try {
      const updatedAction = await apiDiscardAction(actionId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action =>
            action.id === actionId ? updatedAction : action
          ),
        } : null,
        isActionLoading: { ...state.isActionLoading, [actionId]: false },
      }));
      toast.success(`Action ${actionId.substring(0,8)} discarded.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to discard action ${actionId.substring(0,8)}`;
      set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: false }, error: errorMessage }));
      toast.error(errorMessage);
    }
  },

  applyApprovedActions: async () => {
    const inputId = get().activePlanInput?.id;
    if (!inputId) {
      toast.error("No active input for applying actions.");
      return;
    }
    set({ isBatchActionLoading: true, error: null });
    try {
      const response: ApplyApprovedActionsResponse = await apiApplyApprovedActions(inputId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action => {
            const result = response.results.find(r => r.action_id === action.id);
            return result ? { ...action, status: result.status, executionLogs: result.logs } : action;
          }),
        } : null,
        isBatchActionLoading: false,
      }));
      const successCount = response.results.filter(r => r.status === 'confirmed_kept').length;
      const failureCount = response.results.filter(r => r.status === 'execution_failed').length;
      toast.success(`${successCount} actions applied. ${failureCount > 0 ? `${failureCount} failed.` : ''}`);
      if (response.message) toast.info(response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply approved actions';
      set({ isBatchActionLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  discardAllProposedActions: async () => {
    const inputId = get().activePlanInput?.id;
    if (!inputId) {
      toast.error("No active input for discarding actions.");
      return;
    }
    set({ isBatchActionLoading: true, error: null });
    try {
      const response: DiscardAllActionsResponse = await apiDiscardAllProposedActions(inputId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action => {
            const result = response.results.find(r => r.action_id === action.id);
            return result ? { ...action, status: result.status } : action;
          }),
        } : null,
        isBatchActionLoading: false,
      }));
      toast.success(`${response.results.length} actions discarded.`);
      if (response.message) toast.info(response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to discard actions';
      set({ isBatchActionLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  confirmKeepAction: async (actionId: string) => {
    set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: true }, error: null }));
    try {
      const updatedAction = await apiConfirmKeepAction(actionId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action =>
            action.id === actionId ? updatedAction : action
          ),
        } : null,
        isActionLoading: { ...state.isActionLoading, [actionId]: false },
      }));
      toast.success(`Action ${actionId.substring(0,8)} confirmed.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to confirm action ${actionId.substring(0,8)}`;
      set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: false }, error: errorMessage }));
      toast.error(errorMessage);
    }
  },

  revertAction: async (actionId: string) => {
    set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: true }, error: null }));
    try {
      const updatedActionWithLogs = await apiRevertAction(actionId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action =>
            action.id === actionId ? updatedActionWithLogs : action
          ),
        } : null,
        isActionLoading: { ...state.isActionLoading, [actionId]: false },
      }));
      toast.success(`Action ${actionId.substring(0,8)} ${updatedActionWithLogs.status}.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to revert action ${actionId.substring(0,8)}`;
      set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: false }, error: errorMessage }));
      toast.error(errorMessage);
    }
  },

  confirmAllAppliedActions: async () => {
    const inputId = get().activePlanInput?.id;
    if (!inputId) {
      toast.error("No active input for confirming actions.");
      return;
    }
    set({ isBatchActionLoading: true, error: null });
    try {
      const response: ConfirmAllActionsResponse = await apiConfirmAllAppliedActions(inputId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action => {
            const result = response.results.find(r => r.action_id === action.id);
            return result ? { ...action, status: result.status } : action;
          }),
        } : null,
        isBatchActionLoading: false,
      }));
      toast.success(`${response.results.length} actions confirmed.`);
      if (response.message) toast.info(response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to confirm all actions';
      set({ isBatchActionLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  revertAllAppliedActions: async () => {
    const inputId = get().activePlanInput?.id;
    if (!inputId) {
      toast.error("No active input for reverting actions.");
      return;
    }
    set({ isBatchActionLoading: true, error: null });
    try {
      const response: RevertAllActionsResponse = await apiRevertAllAppliedActions(inputId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action => {
            const result = response.results.find(r => r.action_id === action.id);
            return result ? { ...action, status: result.status, executionLogs: result.logs } : action;
          }),
        } : null,
        isBatchActionLoading: false,
      }));
      const successCount = response.results.filter(r => r.status === 'confirmed_reverted').length;
      const failureCount = response.results.filter(r => r.status === 'revert_failed').length;
      toast.success(`${successCount} actions reverted. ${failureCount > 0 ? `${failureCount} failed to revert.` : ''}`);
      if (response.message) toast.info(response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revert all actions';
      set({ isBatchActionLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  fetchLogsForAction: async (actionId: string) => {
    set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: true } }));
    try {
      const logs = await apiGetExecutionLogs(actionId);
      set(state => ({
        activePlanInput: state.activePlanInput ? {
          ...state.activePlanInput,
          aiActions: state.activePlanInput.aiActions.map(action =>
            action.id === actionId ? { ...action, executionLogs: logs } : action
          ),
        } : null,
        isActionLoading: { ...state.isActionLoading, [actionId]: false },
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to fetch logs for action ${actionId.substring(0,8)}`;
      set(state => ({ isActionLoading: { ...state.isActionLoading, [actionId]: false }, error: errorMessage }));
      toast.error(errorMessage);
    }
  },

  restoreSummary: async (summaryInputId: string) => {
    const currentSessionId = get().currentSessionId;
    if (!currentSessionId) {
      toast.error("No active session to restore summary.");
      return;
    }
    set({ isBatchActionLoading: true, error: null });
    try {
      await apiRestoreSummary(currentSessionId, summaryInputId);
      set({ isBatchActionLoading: false });
      toast.success("Summary restored. Original messages re-activated.");
      await get().fetchSessionDetails(currentSessionId, 'refresh');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore summary';
      set({ isBatchActionLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },
});